import * as React from 'react';
import { Text, Title, Group, Container, Box, Button, createStyles } from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from '@mantine/hooks';

// Local Components
import { FormScaffolding, FormScaffoldingProps, FormHandle } from './FormScaffolding';
import { LandingSlide } from './components/slides/LandingSlide';
import { ConfirmPrimaryContactSlide } from './components/slides/ConfirmPrimaryContactSlide';
import { EndingSlide } from './components/slides/EndingSlide';
import { InterpolatedFormCopy } from './components/InterpolatedFormCopy';

// Manifest
import { manifest } from '../../manifests/questionManifest';
import { IFormSection, IManifestField, IManifestFieldHydrated } from '../../manifests/types';

// Helpers
import { hydrateManifestSection } from './helpers/hydrateManifestSection';

// Hooks
import { useWait } from '../../hooks/useWait';

// Types
import { ISurveyTargetPeriod, SurveyStatus } from '../../types';

const useStyles = createStyles((theme) => ({
    headerContainer: {
        background: '#fff',
        padding: '1.25rem',
        border: '1px solid #dee2e6',
        borderRadius: '6px'
    },
    headerContainerProgressive: {
        background: 'transparent',
        padding: '0',
        border: 'none'
    },
    title: {
        fontFamily: `Greycliff CF, ${theme.fontFamily}`,
        textAlign: 'center',
        fontWeight: 900,
        // fontSize: 38,

        [theme.fn.smallerThan('sm')]: {
            // fontSize: 32
        }
    },
    description: {
        paddingTop: theme.spacing.xl,
        marginBottom: theme.spacing.xl,
        borderTop: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[2]}`,
        whiteSpace: 'pre-line'
    },
    wrapper: {
        // marginBottom: '4rem'
    }
}));

enum SLIDES {
    LANDING = 'landing',
    CONFIRM_PRIMARY_CONTACT = 'confirmPrimaryContact',
    FORM = 'form',
    ENDING = 'ending'
}

interface FormProps {
    sections?: IFormSection[];
    description?: string;
    formId?: string;
    clientId?: string;
    title?: string;
    latestNoteId?: string;
    trigger?: string;
    data?: Record<string, any>;
    ancillaryData?: Record<string, any>;
    progressiveForm?: FormScaffoldingProps['progressiveForm'];
    editToggle?: boolean;
    noHeader?: boolean;
    landingSlide?: boolean;
    reviewAnswers?: boolean;
    confirmPrimary?: boolean;
    showReviewStep?: boolean;
    reviewOnly?: boolean;
    surveyStatus?: SurveyStatus;
    onHighlightQuestion?: (qid: string) => void;
    highlightedQuestions?: string[];
    targetPeriod?: ISurveyTargetPeriod;
    // surveyCompletedMessage?: string;
    onComplete?: () => Promise<any>;
    changedPrimaryContactCb?: (val: string) => void;
    ressignPrimaryPending?: boolean;
    furthestStep?: number;
    onSave?: ({ values, furthestStep }: { values: Record<string, any>; furthestStep: number }) => Promise<any>;
    onClickEndingSlideCTA?: () => void;
    isSubmissionSuccessful?: boolean;
}

// export const Form: React.FC<FormProps> = ({
export const Form = React.forwardRef<FormHandle, FormProps>(
    (
        {
            title,
            formId,
            clientId,
            // description,
            sections = [],
            data = {},
            ancillaryData = {},
            targetPeriod,
            noHeader,
            latestNoteId,
            progressiveForm,
            editToggle,
            landingSlide,
            reviewAnswers,
            confirmPrimary,
            showReviewStep,
            furthestStep = 0,
            trigger,
            // surveyCompletedMessage,
            changedPrimaryContactCb,
            ressignPrimaryPending,
            onComplete,
            reviewOnly,
            onHighlightQuestion,
            highlightedQuestions,
            onSave,
            onClickEndingSlideCTA,
            surveyStatus,
            isSubmissionSuccessful,
            ...props
        },
        ref
    ) => {
        const FormRef = React.useRef<FormHandle>(null);
        const { cx, classes } = useStyles();
        const { t } = useTranslation();
        const wait = useWait();
        const [activeSlide, setActiveSlide, removeActiveSlide] = useSessionStorage({
            key: `active-slide-${formId}`,
            defaultValue: landingSlide ? SLIDES.LANDING : SLIDES.FORM
        });
        const [editMode, setEditMode] = React.useState(!editToggle);
        const openDiscardChangesModal = React.useCallback(({ confirmCallback }: { confirmCallback: () => void }) => {
            openConfirmModal({
                title: t('modals.confirmDiscardChanges.title', { ns: 'components' }),
                children: <Text size="sm">{t('modals.confirmDiscardChanges.body', { ns: 'components' })}</Text>,
                labels: {
                    confirm: t('modals.confirmDiscardChanges.confirmCTA', { ns: 'components' }),
                    cancel: t('cancel', { ns: 'common' })
                },
                confirmProps: { color: 'red' },
                onCancel: () => console.log('Cancel'),
                onConfirm: () => {
                    confirmCallback();
                }
            });
        }, []);
        const formSections = React.useMemo(
            () =>
                sections.map((section) => ({
                    ...section,
                    manifest: hydrateManifestSection(section.manifest)
                })),
            [sections, hydrateManifestSection]
        );
        const description = React.useMemo(() => {
            return t(`${trigger}.introductoryMessage`, { ns: 'surveys' });
        }, [t, trigger]);
        const surveyCompletedMessage = React.useMemo(() => {
            return t(`${trigger}.completedMessage`, { ns: 'surveys' });
        }, [t, trigger]);

        const additionalProps = React.useMemo(() => {
            const props: {
                onCancel?: FormScaffoldingProps['onCancel'];
            } = {};

            if (editToggle && editMode) {
                props.onCancel = (form) => {
                    if (form.isDirty()) {
                        openDiscardChangesModal({
                            confirmCallback: () => {
                                form.reset();
                                setEditMode(false);
                            }
                        });
                    } else {
                        form.reset();
                        setEditMode(false);
                    }
                };
            }

            return props;
        }, [editToggle, editMode]);

        React.useEffect(() => {
            //     console.log('surveyStatus', surveyStatus);
            //     console.log('activeSlide', activeSlide);
            //     console.log('`active-slide-${formId}`', `active-slide-${formId}`);
            if (activeSlide === SLIDES.ENDING && surveyStatus && SurveyStatus.REJECTED === surveyStatus) {
                // removeActiveSlide();
                setActiveSlide(SLIDES.FORM);
            }
        }, []);

        React.useEffect(() => {
            if (isSubmissionSuccessful && activeSlide !== SLIDES.ENDING) {
                setActiveSlide(SLIDES.ENDING);
            }
        }, [isSubmissionSuccessful]);

        return (
            <Box className={classes.wrapper}>
                {!noHeader && [(SLIDES.LANDING, SLIDES.FORM)].includes(activeSlide) ? (
                    <Box
                        mb="xl"
                        className={cx(classes.headerContainer, {
                            [classes.headerContainerProgressive]: progressiveForm
                        })}
                    >
                        <Group position="apart">
                            {title ? (
                                <Title className={classes.title} order={2}>
                                    <InterpolatedFormCopy copy={title} {...{ data }} />
                                </Title>
                            ) : (
                                <span></span>
                            )}
                            {editToggle && !editMode ? (
                                <Button onClick={() => setEditMode(!editMode)}>{t('edit', { ns: 'common' })}</Button>
                            ) : null}
                        </Group>
                        {description && !landingSlide ? (
                            <Text mt="lg" className={classes.description}>
                                <InterpolatedFormCopy copy={description} {...{ data }} />
                            </Text>
                        ) : null}
                    </Box>
                ) : null}

                {activeSlide === SLIDES.LANDING ? (
                    <LandingSlide
                        content={description}
                        {...{ data }}
                        dismissCallback={() =>
                            setActiveSlide(confirmPrimary ? SLIDES.CONFIRM_PRIMARY_CONTACT : SLIDES.FORM)
                        }
                    />
                ) : null}
                {activeSlide === SLIDES.CONFIRM_PRIMARY_CONTACT ? (
                    <ConfirmPrimaryContactSlide
                        {...{ data, ancillaryData, formId, clientId, trigger }}
                        onConfirm={() => setActiveSlide(SLIDES.FORM)}
                        onChange={(val: string) => {
                            changedPrimaryContactCb?.(val);
                            sessionStorage.removeItem(`active-step-${formId}`);
                            sessionStorage.removeItem(`active-slide-${formId}`);
                        }}
                        ressignPrimaryPending={ressignPrimaryPending}
                    />
                ) : null}
                {activeSlide === SLIDES.FORM ? (
                    <FormScaffolding
                        ref={ref ?? FormRef}
                        formId={formId}
                        surveyStatus={surveyStatus}
                        clientId={clientId}
                        progressiveForm={progressiveForm}
                        sections={formSections}
                        data={data}
                        latestNoteId={latestNoteId}
                        ancillaryData={ancillaryData}
                        reviewAnswers={reviewAnswers}
                        showReviewStep={showReviewStep}
                        onSave={onSave}
                        reviewOnly={reviewOnly}
                        onHighlightQuestion={onHighlightQuestion}
                        highlightedQuestions={highlightedQuestions}
                        furthestStep={furthestStep}
                        targetPeriod={targetPeriod}
                        trigger={trigger}
                        onComplete={async () => {
                            await (onComplete?.() ?? Promise.resolve());
                            if (
                                surveyCompletedMessage &&
                                progressiveForm &&
                                typeof isSubmissionSuccessful === 'undefined'
                            ) {
                                setActiveSlide(SLIDES.ENDING);
                            }
                        }}
                        {...additionalProps}
                        {...{ editMode }}
                    ></FormScaffolding>
                ) : null}
                {activeSlide === SLIDES.ENDING ? (
                    <EndingSlide
                        content={surveyCompletedMessage}
                        trigger={trigger}
                        {...{ data }}
                        onActionCb={async () => {
                            onClickEndingSlideCTA?.();
                            setTimeout(removeActiveSlide, 500);
                        }}
                    />
                ) : null}
            </Box>
        );
    }
);
